import { Box, Button, Dialog, Divider, Drawer, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import FilterSelectionContainer from '../../../Components/FilterSelectionContainer';
import { Checkbox, Tag, theme } from 'antd';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { HelperTexts } from '../../../Utils/helper/HelperTexts';
import CultService from '../../../Services/CultService';
import { FilterListIcon } from '../../../Components/Icons/Icons';
import { AppColors } from '../../../Utils/helper/AppColors';
import CustomTag from '../../../Components/CustomTag';
import SearchBar from '../../../Components/SearchBar';
import InfiniteScrollDialog from '../../../Components/InfiniteScrollDialog'; // import your dialog
import { DatePicker } from 'antd';
import { FiCalendar } from 'react-icons/fi';
import dayjs from 'dayjs';
import { getColorSettings } from '../../../Utils/helper/commonUtils';

const { RangePicker } = DatePicker;

export default function RecordingsFilter({ setFilterQuery, campaigns }) {
	const [showFilter, setShowFilter] = useState(false);
	const [filters, setFilters] = useState({});
	const [selected, setSelected] = useState({});
	const company = useSelector((state) => state?.app?.company);

	const [categorySearches, setCategorySearches] = useState({});
	const [initialFilters, setInitialFilters] = useState({});

	const [showInfiniteDialog, setShowInfiniteDialog] = useState(false);

	// Infinite scroll states for users
	const [userPage, setUserPage] = useState(2);
	const [usersLoading, setUsersLoading] = useState(false);
	const [hasMoreUsers, setHasMoreUsers] = useState(true);

	const getUsers = async () => {
		try {
			let res = await CultService.getAllUsersPaginatedv1({
				params: { page: 1, limit: 50, companyId: company?._id, filterQuery: {} },
			});
			if (res.status) {
				setFilters((prev) => ({ ...prev, users: res.data.users }));
				setInitialFilters((prev) => ({ ...prev, users: res.data.users }));
			}
		} catch (err) {
			toast.error(HelperTexts.SOMETHING_WENT_WRONG);
		}
	};

	// console.log('selected', initialFilters);

	const handleSelectFilter = (category, value) => {
		setSelected((prevFilters) => {
			const categoryValues = prevFilters[category] || [];
			if (categoryValues.includes(value)) {
				const updatedCategoryValues = categoryValues.filter((item) => item !== value);
				if (updatedCategoryValues.length === 0) {
					const { [category]: removed, ...rest } = prevFilters;
					return rest;
				}

				return {
					...prevFilters,
					[category]: updatedCategoryValues,
				};
			} else {
				return {
					...prevFilters,
					[category]: [...categoryValues, value],
				};
			}
		});
	};

	const handleApplyChanges = () => {
		setFilterQuery(selected);
		setShowFilter(false);
	};

	useEffect(() => {
		if (company) {
			getUsers();
		}
	}, [company]);

	// useEffect(() => {
	// 	if (campaigns?.length > 0) {
	// 		setFilters((prev) => ({ ...prev, campaigns }));
	// 	}
	// }, [campaigns]);

	const filterCategory = (category, searchTerm) => {
		if (!initialFilters || !initialFilters[category]) return [];
		// For users, you may want to search by name/email
		if (category === 'users') {
			return initialFilters[category].filter(
				(item) =>
					item.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					item.email?.toLowerCase().includes(searchTerm.toLowerCase()),
			);
		}
		// For other filters, simple string match
		return initialFilters[category].filter((item) =>
			(item?.basicDetails?.name || item?.firstName || item?.toString()).toLowerCase().includes(searchTerm.toLowerCase()),
		);
	};

	const handleCategorySearch = (category, searchTerm) => {
		setCategorySearches((prev) => ({ ...prev, [category]: searchTerm }));
		setFilters((prev) => ({
			...prev,
			[category]: filterCategory(category, searchTerm),
		}));
	};

	const handleResetFilter = (filter) => {
		setSelected((prev) => {
			const updated = { ...prev };
			delete updated[filter];
			return updated;
		});
	};

	const handleSelectAllFilter = (filter) => {
		setSelected((prev) => ({
			...prev,
			[filter]: [...filters[filter]],
		}));
	};

	// Fetch users for infinite dialog
	const fetchMoreUsers = async () => {
		if (!company?._id || usersLoading || !hasMoreUsers) return;
		setUsersLoading(true);
		try {
			const res = await CultService.getAllUsersPaginatedv1({
				params: {
					page: userPage,
					limit: 50,
					companyId: company._id,
					filterQuery: {},
				},
			});
			if (res.status) {
				const newUsers = res.data.users || [];
				setFilters((prev) => ({
					...prev,
					users: userPage === 1 ? newUsers : [...(prev.users || []), ...newUsers],
				}));
				setInitialFilters((prev) => ({
					...prev,
					users: userPage === 1 ? newUsers : [...(prev.users || []), ...newUsers],
				}));
				const { currentPage, totalPages } = res.data.pagination || {};
				setHasMoreUsers(currentPage < totalPages);
				setUserPage(userPage + 1);
			} else {
				setHasMoreUsers(false);
			}
		} catch (err) {
			setHasMoreUsers(false);
			toast.error(HelperTexts.SOMETHING_WENT_WRONG);
		}
		setUsersLoading(false);
	};

	// Date filter states
	const [customRange, setCustomRange] = useState([null, null]);
	const [selectedDate, setSelectedDate] = useState('');

	const handleDateSelect = (value) => {
		setSelectedDate(value);

		const today = dayjs().startOf('day');
		let from, to;

		if (value === 'today') {
			from = today.format('YYYY-MM-DD');
			to = today.format('YYYY-MM-DD');
			setSelected((prev) => ({
				...prev,
				date: { type: 'today', from, to },
			}));
		} else if (value === 'yesterday') {
			from = today.subtract(1, 'day').format('YYYY-MM-DD');
			to = today.format('YYYY-MM-DD');
			setSelected((prev) => ({
				...prev,
				date: { type: 'yesterday', from, to },
			}));
		} else if (value === '7days') {
			from = today.subtract(6, 'day').format('YYYY-MM-DD');
			to = today.format('YYYY-MM-DD');
			setSelected((prev) => ({
				...prev,
				date: { type: '7days', from, to },
			}));
		} else if (value === '15days') {
			from = today.subtract(14, 'day').format('YYYY-MM-DD');
			to = today.format('YYYY-MM-DD');
			setSelected((prev) => ({
				...prev,
				date: { type: '15days', from, to },
			}));
		}
		// For custom, handled separately
	};

	const handleCustomDateApply = () => {
		if (customRange[0] && customRange[1]) {
			setSelected((prev) => ({
				...prev,
				date: {
					type: 'custom',
					from: dayjs(customRange[0]).format('YYYY-MM-DD'),
					to: dayjs(customRange[1]).format('YYYY-MM-DD'),
				},
			}));
		}
	};

	console.log('', filters);

	const localSettingColors = getColorSettings();
	
   return (
      
		<>
			<Button
				onClick={(e) => setShowFilter(true)}
				variant="contained"
				color='info'
				startIcon={<FilterListIcon color={AppColors.WHITE} />}
			>
				Filters
			</Button>

			<Dialog
				anchor="right"
				// sx={{ zIndex: 9999 }}
				open={showFilter}
				onClose={() => setShowFilter(false)}
				maxWidth={false} // disables default max-width constraints
				PaperProps={{
					sx: {
						width: '90vw',
						height: '90vh',
						borderRadius: '10px',
					},
				}}
			>
				<FilterSelectionContainer
					title="conversations"
					onClose={() => setShowFilter(false)}
					handleApplyChanges={handleApplyChanges}
					resetDisable={Object.keys(selected || {})?.length || 0}
					width={'100vw'}
				>
					<Stack gap={2} divider={<Divider />}>
						{/* Date Filter UI at the top */}
						<Box sx={{ backgroundColor: '#ffffff', borderRadius: '5px', padding: '20px', minWidth: 300 }}>
							<Typography
								sx={theme => ({
									fontWeight: 600,
									fontSize: 16,
									color: theme.palette.primary.darker_primary,
									mb: 2,
								})}
							>
								Date
							</Typography>
							<RadioGroup value={selectedDate} onChange={(e) => handleDateSelect(e.target.value)}>
								<Stack direction="row" flexWrap="wrap" gap={2}>
									<FormControlLabel
										value="today"
										control={<Radio color="primary" />}
										label="Today"
										sx={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14 }}
									/>
									<FormControlLabel
										value="yesterday"
										control={<Radio color="primary" />}
										label="Yesterday"
										sx={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14 }}
									/>
									<FormControlLabel
										value="7days"
										control={<Radio color="primary" />}
										label="Last 7 Days"
										sx={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14 }}
									/>
									<FormControlLabel
										value="15days"
										control={<Radio color="primary" />}
										label="Last 15 Days"
										sx={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14 }}
									/>
									<FormControlLabel
										value="custom"
										control={<Radio color="primary" />}
										label="Custom"
										sx={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14 }}
									/>
								</Stack>
							</RadioGroup>
							{selectedDate === 'custom' && (
								<Stack p={2} spacing={2} maxWidth={600}>
									<RangePicker
										value={customRange}
										onChange={(val) => setCustomRange(val || [null, null])}
										format="YYYY-MM-DD"
										getPopupContainer={(node) => node.parentNode}
										style={{ width: '100%' }}
										suffixIcon={<FiCalendar color="#B9B9F7" size={18} />}
									/>
									<Button
										variant="contained"
										color='info'
										onClick={handleCustomDateApply}
										disabled={!customRange[0] || !customRange[1]}
									>
										Apply
									</Button>
								</Stack>
							)}
						</Box>

						{/* Your filters */}
						{Object?.keys(filters || {})?.map((filter, fIndex) => (
							<Stack
								key={fIndex}
								sx={{
									backgroundColor: '#ffffff',
									border: '1px solid #ECE7FC',
									padding: '20px',
									borderRadius: '5px',
								}}
								gap={2}
							>
								<Stack direction="row" alignItems="center" justifyContent="space-between">
									<Stack direction="row" alignItems="center" gap={2}>
										<Typography
											textTransform="capitalize"
											variant="subtitle1"
											sx={{
												fontFamily: 'Poppins',
												fontWeight: 600,
												fontSize: 18,
												lineHeight: '150%',
												color: '#1A1A1A',
											}}
										>
											{filter === 'users' ? 'Employee' : filter}
										</Typography>
										<SearchBar
											search={categorySearches[filter] || ''}
											setSearch={(value) => handleCategorySearch(filter, value)}
											color={localSettingColors.fontColor}
											bgColor={localSettingColors.quinary}
											width={'607px'}
											variant={'small'}
											borderRadius={'5px'}
											placeholder={`Enter  name,  email, time zone or department`}
										/>
									</Stack>
									<Stack direction="row" gap={1}>
										<Button
											size="small"
											variant="text"
											sx={theme => ({
												color: theme.palette.primary.darker_primary,
												fontSize: 14,
												fontWeight: 600,
												lineHeight: '150%',
												fontFamily: 'Poppins',
												backgroundColor: 'transparent',
												boxShadow: 'none',
												textDecoration: 'none',
												'&:hover': {
													backgroundColor: 'transparent',
													color: theme.palette.primary.darker_primary,
													textDecoration: 'none',
													boxShadow: 'none',
												},
												cursor: 'pointer',
											})}
											onClick={() => handleResetFilter(filter)}
											disabled={!selected[filter]?.length}
										>
											Reset
										</Button>
										<Button
											size="small"
											variant="outlined"
											sx={theme => ({
												color: theme.palette.primary.primary_shade,
												fontSize: 12,
												fontWeight: 600,
												lineHeight: '100%',
												fontFamily: 'Poppins',
												border: `2px solid ${theme.palette.primary.primary_shade}`,
												borderRadius: '5px',
												'&:hover': {
													border: `2px solid ${theme.palette.primary.primary_shade}`,
													borderRadius: '5px',
													backgroundColor: '#F5F1FF',
												},
											})}
											onClick={() => handleSelectAllFilter(filter)}
											disabled={selected[filter]?.length === filters[filter]?.length}
										>
											Select All
										</Button>
									</Stack>
								</Stack>

								{/* Conditional rendering for users */}
								{filter === 'users' ? (
									<>
										<Stack
											gap={'22px'}
											sx={{
												display: 'flex',
												flexDirection: 'row',
												flexWrap: 'wrap',
												alignItems: 'flex-start',
											}}
										>
											{filters[filter]?.slice(0, 4).map((item, iIndex) => {
												const isSelected = selected[filter]?.includes(item);
												return (
													<Box
														key={iIndex}
														sx={theme => ({
															minWidth: 280,
															maxWidth: 418,
															height: 100,
															background: isSelected ? theme.palette.primary.lightest_shade : '#FFFFFF',
															borderRadius: '5px',
															border: isSelected ? `2px solid ${theme.palette.primary.primary_shade}` : `1px solid ${theme.palette.primary.lighter_shade}`,
															position: 'relative',
															p: '15px',

															display: 'flex',
															flexDirection: 'column',
															justifyContent: 'space-between',
														})}
													>
														{/* Checkbox in top-right */}
														<Checkbox
															checked={isSelected}
															onChange={() => handleSelectFilter(filter, item)}
															className="custom-inline-checkbox"
															style={{
																position: 'absolute',
																top: -4,
																right: -1,
															}}
														/>
														{/* User Info */}
														<Box>
															<Typography
																sx={theme => ({
																	fontFamily: 'Poppins',
																	fontWeight: 600,
																	fontSize: 14,
																	lineHeight: '150%',
																	color: theme.palette.primary.darker_primary,
																})}
															>
																{item?.firstName}
															</Typography>
															<Stack direction="row" gap={1} alignItems="center" justifyContent={'space-between'}>
																<Typography
																	sx={theme => ({
																		fontFamily: 'Poppins',
																		fontWeight: 400,
																		fontSize: 12,
																		lineHeight: '115%',
																		color: theme.palette.primary.darker_primary,
																	})}
																>
																	{item?.email}
																</Typography>
																<Typography
																	sx={theme => ({
																		fontFamily: 'Poppins',
																		fontWeight: 500,
																		fontSize: 8,
																		lineHeight: '121%',
																		color: isSelected ? theme.palette.primary.darker_primary : AppColors.HEADING_TITLE4,
																	})}
																>
																	{/* {item.dateOfJoining ? `Joined: ${new Date(item.dateOfJoining).toLocaleDateString()}` : ''} */}
																	{item?.timezone}
																</Typography>
															</Stack>
														</Box>

														<Stack direction="row" gap={1} alignItems="center" marginTop={2}>
															{item?.department && (
																<CustomTag
																	tagTitle={item?.department}
																	borderRadius={'3px'}
																	maxWidth={'111px'}
																	minWidth={'111px'}
																	borderColor={isSelected ? localSettingColors.buttonBackground : localSettingColors.quaternary}
																	backgroundColor={isSelected ? '#FFFFFF' : localSettingColors.quinary}
																	fontSize={'10px'}
																	fontWeight={500}
																	lineHeight={'121%'}
																/>
															)}
															{item?.role && (
																<CustomTag
																	tagTitle={item?.role}
																	borderRadius={'5px'}
																	maxWidth={'111px'}
																	minWidth={'111px'}
																	borderColor={isSelected ? localSettingColors.buttonBackground : localSettingColors.quaternary}
																	backgroundColor={isSelected ? '#FFFFFF' : localSettingColors.quinary}
																	fontSize={'10px'}
																	fontWeight={500}
																	lineHeight={'121%'}
																/>
															)}
														</Stack>
													</Box>
												);
											})}
										</Stack>
										<Stack alignItems={'flex-end'}>
											<Button
												size="small"
												variant="text"
												sx={theme => ({
													color: localSettingColors.fontColor,
													fontSize: 12,
													fontWeight: 400,
													lineHeight: '150%',
													fontFamily: 'Poppins',
													backgroundColor: 'transparent',
													boxShadow: 'none',
													textDecoration: 'none',
													'&:hover': {
														backgroundColor: 'transparent',
														color: theme.palette.primary.darker_primary,
														textDecoration: 'none',
														boxShadow: 'none',
													},
													cursor: 'pointer',
												})}
												onClick={() => setShowInfiniteDialog(true)}
											>
												Show More
											</Button>
										</Stack>
									</>
								) : (
									// Default rendering for other filters
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
										{filters?.[filter]?.map((item, iIndex) => (
											<Box
												key={iIndex}
												sx={theme => ({
													backgroundColor: theme.palette.primary.lightest_shade,
													alignItems: 'center',
													padding: '6px 10px',
													borderRadius: '5px',
												})}
											>
												<Stack direction="row" alignItems="center" gap={1}>
													<Checkbox
														checked={selected[filter]?.includes(item) || false}
														onChange={() => handleSelectFilter(filter, item)}
														className="purple-checkbox"
													/>
													<Typography
														sx={theme => ({
															fontFamily: 'Poppins',
															fontWeight: 400,
															fontSize: 14,
															lineHeight: '150%',
															color: theme.palette.primary.darker_primary,
														})}
													>
														{item?.basicDetails?.name || item.firstName + ' ' + item?.lastName || item}
													</Typography>
												</Stack>
											</Box>
										))}
									</Box>
								)}
							</Stack>
						))}
					</Stack>
				</FilterSelectionContainer>
			</Dialog>

			{/* Infinite Scroll Dialog for Users */}
			<InfiniteScrollDialog
				open={showInfiniteDialog}
				onClose={() => setShowInfiniteDialog(false)}
				title="Employee"
				search={categorySearches['users'] || ''}
				setSearch={(v) => handleCategorySearch('users', v)}
				onReset={() => handleResetFilter('users')}
				onSelectAll={() => handleSelectAllFilter('users')}
				selected={selected['users'] || []}
				items={filters['users'] || []}
				renderItem={(item, iIndex) => {
					const isSelected = selected['users']?.includes(item);
					return (
						<Box
							key={iIndex}
							sx={theme => ({
								minWidth: 280,
								maxWidth: 418,
								height: 100,
								background: isSelected ? theme.palette.primary.lightest_shade : '#FFFFFF',
								borderRadius: '5px',
								border: isSelected ? `2px solid ${theme.palette.primary.primary_shade}` : `1px solid ${theme.palette.primary.lighter_shade}`,
								position: 'relative',
								p: '15px',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'space-between',
							})}
						>
							<Checkbox
								checked={isSelected}
								onChange={() => handleSelectFilter('users', item)}
								className="custom-inline-checkbox"
								style={{
									position: 'absolute',
									top: -4,
									right: -1,
								}}
							/>
							<Box>
								<Typography
									sx={theme => ({
										fontFamily: 'Poppins',
										fontWeight: 600,
										fontSize: 14,
										lineHeight: '150%',
										color: theme.palette.primary.darker_primary,
									})}
								>
									{item?.firstName}
								</Typography>
								<Stack direction="row" gap={1} alignItems="center" justifyContent={'space-between'}>
									<Typography
										sx={theme => ({
											fontWeight: 400,
											fontSize: 12,
											lineHeight: '115%',
											color: theme.palette.primary.darker_primary,
										})}
									>
										{item?.email}
									</Typography>
									<Typography
										sx={theme => ({
											fontFamily: 'Poppins',
											fontWeight: 500,
											fontSize: 8,
											lineHeight: '121%',
											color: isSelected ? theme.palette.primary.darker_primary : AppColors.HEADING_TITLE4,
										})}
									>
										{item?.timezone}
									</Typography>
								</Stack>
							</Box>
							<Stack direction="row" gap={1} alignItems="center" marginTop={2}>
								{item?.department && (
									<CustomTag
										tagTitle={item?.department}
										borderRadius={'3px'}
										maxWidth={'111px'}
										minWidth={'111px'}
										borderColor={isSelected ? localSettingColors.buttonBackground : localSettingColors.quaternary}
										backgroundColor={isSelected ? '#FFFFFF' : localSettingColors.quinary}
										fontSize={'10px'}
										fontWeight={500}
										lineHeight={'121%'}
									/>
								)}
								{item?.role && (
									<CustomTag
										tagTitle={item?.role}
										borderRadius={'5px'}
										maxWidth={'111px'}
										minWidth={'111px'}
										borderColor={isSelected ? localSettingColors.buttonBackground : localSettingColors.quaternary}
										backgroundColor={isSelected ? '#FFFFFF' : localSettingColors.quinary}
										fontSize={'10px'}
										fontWeight={500}
										lineHeight={'121%'}
									/>
								)}
							</Stack>
						</Box>
					);
				}}
				fetchMore={fetchMoreUsers}
				loading={usersLoading}
				hasMore={hasMoreUsers}
			/>

			

			{/* <Dialog
				open={openPicker}
				onClose={() => setOpenPicker(false)}
				PaperProps={{
					sx: {
						width: '400px',
						borderRadius: '10px',
					},
				}}
			>
				<Box sx={{ backgroundColor: theme.palette.primary.lightest_shade, borderRadius: '5px', p: 2, minWidth: 300 }}>
					<Typography
						sx={{
							fontFamily: 'Poppins',
							fontWeight: 600,
							fontSize: 16,
							color: '#683FC7',
							mb: 2,
						}}
					>
						Date
					</Typography>
					<RadioGroup value={selectedDate} onChange={(e) => handleDateSelect(e.target.value)}>
						<Stack direction="row" flexWrap="wrap" gap={2}>
							<FormControlLabel
								value="today"
								control={<Radio color="primary" />}
								label="Today"
								sx={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14 }}
							/>
							<FormControlLabel
								value="yesterday"
								control={<Radio color="primary" />}
								label="Yesterday"
								sx={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14 }}
							/>
							<FormControlLabel
								value="7days"
								control={<Radio color="primary" />}
								label="Last 7 Days"
								sx={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14 }}
							/>
							<FormControlLabel
								value="15days"
								control={<Radio color="primary" />}
								label="Last 15 Days"
								sx={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14 }}
							/>
							<FormControlLabel
								value="custom"
								control={<Radio color="primary" />}
								label="Custom"
								sx={{ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14 }}
							/>
						</Stack>
					</RadioGroup>
					{selectedDate === 'custom' && (
						<Stack p={2} spacing={2}>
							<RangePicker
								value={customRange}
								onChange={(val) => setCustomRange(val || [null, null])}
								format="YYYY-MM-DD"
								getPopupContainer={(node) => node.parentNode}
								style={{ width: '100%' }}
								suffixIcon={<FiCalendar color="#B9B9F7" size={18} />}
							/>
							<Button
								variant="contained"
								onClick={handleCustomDateApply}
								disabled={!customRange[0] || !customRange[1]}
							>
								Apply
							</Button>
						</Stack>
					)}
				</Box>
			</Dialog> */}
		</>
	);
}
